import _ from 'lodash';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useMemo,
  useState,
} from 'react';
import { useTranslations } from 'use-intl';

import navConfig from '../constants/navigationLinks';
import useUser from '../hooks/useUser';
import NavigationLink from '../interfaces/NavigationLink';
import Permissions from '../shared/enums/Permissions';

export interface NavListContextType {
  navigationLinks: NavigationLink[];
  allowedPages: string[];
  onActive: (content: NavigationLink) => void;
  fullScreenMode: boolean;
  setFullScreenMode: (value: boolean) => void;
  flushMode: boolean;
  setFlushMode: (value: boolean) => void;
}

/**
 * Drop internal-only entries, and any group they leave empty.
 *
 * Recursive because the flag sits on the page, not the group: Health's Overview is internal-only
 * while Campaign Ops beneath it is deliberately tenant-facing. Removed rather than locked — unlike
 * an unsubscribed or role-locked module there is nothing a tenant can do to gain access, so showing
 * it would advertise something unobtainable.
 */
export const stripInternalOnly = (list: NavigationLink[]): NavigationLink[] =>
  list.reduce((acc, item) => {
    if (item.internalOnly) return acc;
    if (Array.isArray(item.content)) {
      const content = stripInternalOnly(item.content);
      if (content.length) acc.push({ ...item, content });
      return acc;
    }
    acc.push(item);
    return acc;
  }, [] as NavigationLink[]);

const NavigationLinkContext = createContext<NavListContextType>({
  navigationLinks: [],
  allowedPages: [],
  onActive(content) {},
  fullScreenMode: false,
  setFullScreenMode(value) {},
  flushMode: false,
  setFlushMode(value) {},
});

export const NavigationLinkProvider: FC<PropsWithChildren> = ({ children }) => {
  const { userPermissions, subscribedPermissions, currentUser } = useUser();
  const tNav = useTranslations('nav');
  // The one place internal-only entries are removed; both memos below read this instead of navConfig.
  const visibleConfig = useMemo(
    () => (currentUser?._isInternalUser ? navConfig : stripInternalOnly(navConfig)),
    [currentUser?._isInternalUser],
  );
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [flushMode, setFlushMode] = useState(false);

  const navigationLinks = useMemo(() => {
    // Filters a subtree down to the items the user can access. Used to resolve
    // the children of a module the user has at least partial access to.
    const getAllowedNavList = (list: NavigationLink[]): NavigationLink[] => {
      return list.reduce((acc, val) => {
        const hasPermission = _.isEmpty(
          _.difference(val.requiredPermissions, userPermissions)
        );
        if (!hasPermission) return acc;
        if (typeof val.content === 'string') acc.push(val);
        if (Array.isArray(val.content)) {
          const content = getAllowedNavList(val.content);
          content.length && acc.push({ ...val, content });
        }
        return acc;
      }, [] as NavigationLink[]);
    };

    // Walks a subtree and gathers every permission a leaf route requires.
    // Parent groups frequently declare `requiredPermissions: []` and delegate
    // the real gating to their children, so the parent's own list alone is
    // not a reliable signal for the subscribed-vs-role distinction.
    const collectLeafPermissions = (item: NavigationLink): string[] => {
      const own = item.requiredPermissions ?? [];
      if (typeof item.content === 'string') return own;
      const fromChildren = item.content.flatMap(collectLeafPermissions);
      return [...own, ...fromChildren];
    };

    // Locked modules are split into two cases so the UI can communicate the
    // remediation path: `notSubscribed` (business needs to purchase) vs
    // `locked` (user's role doesn't grant access). When at least one of the
    // required permissions is subscribed, we treat it as a role-level lock —
    // the user could potentially get access via an admin.
    const lockedFlags = (required: string[]) => {
      const unique = _.uniq(required);
      if (unique.length === 0) return { locked: true };
      const anySubscribed = unique.some((p) => subscribedPermissions.includes(p));
      return anySubscribed
        ? { locked: true }
        : { locked: true, notSubscribed: true };
    };

    // Every top-level module is kept. If the user has no accessible route
    // anywhere within a module, it is surfaced in a locked state instead of
    // being hidden.
    return visibleConfig.map((module): NavigationLink => {
      const hasModulePermission = _.isEmpty(
        _.difference(module.requiredPermissions, userPermissions)
      );

      if (typeof module.content === 'string') {
        return hasModulePermission
          ? module
          : { ...module, ...lockedFlags(module.requiredPermissions) };
      }

      const allowedChildren = hasModulePermission
        ? getAllowedNavList(module.content)
        : [];

      if (allowedChildren.length) {
        return { ...module, content: allowedChildren };
      }

      return {
        ...module,
        content: [],
        ...lockedFlags(collectLeafPermissions(module)),
      };
    });
  }, [userPermissions, subscribedPermissions, visibleConfig]);

  const allowedPages = useMemo(() => {
    const getAllowedNavList = (list: NavigationLink[]) => {
      return list.reduce((acc, val) => {
        const hasPermission = _.isEmpty(
          _.difference(val.requiredPermissions, userPermissions)
        );

        if (!hasPermission) return acc;
        if (typeof val.content === 'string') acc.push(val.content);
        if (_.isArray(val.content)) acc.push(...getAllowedNavList(val.content));

        return acc;
      }, [] as string[]);
    };

    const canConfigure = [Permissions.CONFIG_MANAGE, Permissions.DATA_MANAGE].some((permission) =>
      userPermissions.includes(permission)
    );

    return [...getAllowedNavList(visibleConfig), canConfigure ? '/settings' : '/settings/preferences'];
  }, [userPermissions, visibleConfig]);

  const handleOnActivePage = (content: NavigationLink) => {
    const label = tNav.has(content.label) ? tNav(content.label) : content.label;
    window.document.title = `${label} - CASA Retail AI`;
  };

  return (
    <NavigationLinkContext.Provider
      value={{
        navigationLinks,
        allowedPages,
        setFullScreenMode,
        onActive: handleOnActivePage,
        fullScreenMode,
        flushMode,
        setFlushMode,
      }}
    >
      {children}
    </NavigationLinkContext.Provider>
  );
};

export default NavigationLinkContext;
